/**
 * Integrations Tab Component
 * 
 * Displays and manages data source integrations with health monitoring,
 * sync controls, and configuration management.
 * Extracted from IntegrationsDashboard.tsx for use in unified External Data Dashboard.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Badge, LoadingSpinner, EmptyState, usePermissions, useConfig } from '@tracekit/core';
import { useWorkspace, getPolicyPack, getProject, listSources, triggerSync, updateSourceMetadata, registerAPIIntegrationSource, getSAMApiKey, resolveSamGovIntegrationState, previewSyncAPIIntegration, getEntitySources } from '@tracekit/core';
import { listJobs, getJob } from '@tracekit/core/api/jobs';
import {
  dataSourceRegistry,
  DataSourceType,
  HealthCheckResult,
  fetchFromDataSource,
} from '../../../services/dataSourceRegistry';
import { DataSourceConfig } from '../../../services/dataSourceIntegrations';
import { loadDataSourceConfigsFromPolicyPack } from '../../../utils/policyPackConfigLoader';
import { RSS_FEED_REGISTRY } from '../../../services/rssFeedRegistry';
import { SyncSettingsModal } from '../SyncSettingsModal';
import { CredentialManagementModal } from '../CredentialManagementModal';
import { SyncStatusBadge } from '../SyncStatusBadge';
import { GeographicFilter as GeographicFilterComponent } from '../GeographicFilter';
import { ServiceLineFilter } from '../ServiceLineFilter';
import { requiresCredentials, getCredentialFields } from '../../../utils/credentialFields';
import { useExternalData } from '../../../contexts/ExternalDataContext';
import { useGeographicZone } from '../../../contexts/GeographicZoneContext';
import { dataSourceMatchesGeographicZone } from '../../../utils/countryAccess';
import {
  entityCountsBySourceId,
  findSourceByIntegrationType,
} from '../../../utils/integrationSourceMatch';
import {
  Database,
  RefreshCw,
  Play,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Filter,
  Key,
  Settings,
  TrendingUp,
} from 'lucide-react';

interface IntegrationStatus {
  source: DataSourceType;
  enabled: boolean;
  health: HealthCheckResult | null;
  lastSync: string | null;
  opportunitiesSynced: number;
  config: DataSourceConfig;
}

// Helper to ensure source exists
async function ensureSourceExists(
  projectId: string,
  source: DataSourceType,
  config: DataSourceConfig
): Promise<string> {
  const sources = await listSources(projectId);
  let existingSource = findSourceByIntegrationType(sources, source);
  
  if (!existingSource) {
    const sourceName = source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const apiConfig: Record<string, any> = {};
    
    if (source === 'samgov' && config.api_key) {
      apiConfig.api_key = config.api_key;
    }
    if (source === 'rss_feeds') {
      apiConfig.feed_urls = RSS_FEED_REGISTRY.map(feed => feed.url);
    }
    
    const filters: Record<string, any> = {};
    if (config.keywords) filters.keywords = config.keywords;
    if (config.startDate) filters.posted_from = config.startDate;
    if (config.limit) filters.limit = config.limit;
    
    existingSource = await registerAPIIntegrationSource({
      project_id: projectId,
      name: source,
      description: `${sourceName} API Integration`,
      integration_type: source as any,
      api_config: apiConfig,
      filters: filters,
      sync_frequency: 'daily',
      enabled: true,
    });
  }
  
  return existingSource.id;
}

// Helper to get metrics
function getMetrics(
  integration: IntegrationStatus,
  sourceMetrics: Record<string, any>
): {
  responseTime: string;
  lastSync: string;
  itemCount: number;
  status: string;
  statusColor: string;
} {
  const dynamicMetrics = sourceMetrics[integration.source];
  
  return {
    responseTime: dynamicMetrics?.responseTime 
      ? `${dynamicMetrics.responseTime}ms`
      : integration.health?.response_time_ms 
      ? `${integration.health.response_time_ms}ms`
      : 'N/A',
    lastSync: dynamicMetrics?.lastSync 
      ? new Date(dynamicMetrics.lastSync).toLocaleString()
      : integration.lastSync || 'Never',
    itemCount: dynamicMetrics?.itemCount ?? integration.opportunitiesSynced,
    status: dynamicMetrics?.status && dynamicMetrics.status !== 'unknown'
      ? dynamicMetrics.status 
      : integration.enabled 
      ? (integration.health?.status && integration.health.status !== 'unknown' 
          ? integration.health.status 
          : (integration.lastSync || integration.opportunitiesSynced > 0 
              ? 'healthy' 
              : 'Unknown'))
      : 'Disabled',
    statusColor: dynamicMetrics?.status === 'healthy' || integration.health?.healthy
      ? 'text-green-600'
      : dynamicMetrics?.status === 'error' || integration.health?.status === 'error'
      ? 'text-red-600'
      : 'text-yellow-600',
  };
}

function IntegrationsTab() {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const { roles } = usePermissions();
  const config = useConfig();
  const allowedTechRoles = config.customization?.integrationsTechRoles ?? ['admin_tech'];
  const canPreviewSync = roles.some((r: { role_name?: string }) => {
    const name = (r.role_name || '').toLowerCase().replace(/\s+/g, '_');
    return allowedTechRoles.some((allowed: string) => name === allowed.toLowerCase());
  });

  const { 
    geographicFilter, 
    setGeographicFilter,
    serviceLineFilter,
    setServiceLineFilter,
    selectedGeographicZone,
    searchQuery,
    setSearchQuery,
  } = useExternalData();
  const { userCountryAccess } = useGeographicZone();
  
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'unhealthy' | 'disabled'>('all');
  const [expandedSource, setExpandedSource] = useState<DataSourceType | null>(null);
  const [previewData, setPreviewData] = useState<Record<string, { opportunities: any[]; awards: any[]; error?: string }>>({});
  const [sourceMetrics, setSourceMetrics] = useState<Record<string, {
    responseTime: number;
    lastSync: string;
    itemCount: number;
    status: 'healthy' | 'degraded' | 'error';
  }>>({});
  
  // Modal states
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncModalSource, setSyncModalSource] = useState<{
    source: DataSourceType;
    sourceId: string;
    sourceName: string;
    integrationType: string;
    config: DataSourceConfig;
  } | null>(null);
  
  const [credentialModalOpen, setCredentialModalOpen] = useState(false);
  const [credentialModalSource, setCredentialModalSource] = useState<{
    source: DataSourceType;
    sourceId: string;
    sourceName: string;
    integrationType: string;
    config: DataSourceConfig;
  } | null>(null);
  
  const allSources = dataSourceRegistry.getAll();
  
  // Load persisted sources from database
  const { data: persistedSources = [], isLoading: loadingSources } = useQuery({
    queryKey: ['integration-sources', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const sources = await listSources(projectId);
      return sources.filter(s => s.type === 'api_integration');
    },
    enabled: !!projectId,
  });
  
  // Load project and policy pack
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
  });

  const { data: policyPack } = useQuery({
    queryKey: ['policy-pack', project?.policy_pack_id],
    queryFn: () => getPolicyPack(project!.policy_pack_id!),
    enabled: !!project?.policy_pack_id,
  });

  // Load configurations from policy pack, persisted sources, and defaults
  const { data: configurations, isLoading: loadingConfigs } = useQuery({
    queryKey: ['integration-configs', projectId, project?.policy_pack_id],
    queryFn: async () => {
      let policyPackConfigs: Record<DataSourceType, DataSourceConfig> | null = null;
      if (project?.policy_pack_id) {
        try {
          policyPackConfigs = await loadDataSourceConfigsFromPolicyPack(
            project.policy_pack_id,
            projectId!
          );
        } catch (error) {
          console.warn('[IntegrationsTab] Failed to load configs from policy pack:', error);
        }
      }

      const sources = await listSources(projectId!);
      const apiIntegrationSources = sources.filter(s => s.type === 'api_integration');
      
      const defaultConfigs: Record<DataSourceType, DataSourceConfig> = {
        canada_grants: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental', 'assessment'] } },
        canada_tenders: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'] } },
        samgov: { ...resolveSamGovIntegrationState({ defaultEnabled: true }), sync_frequency: 'daily', filters: { keywords: ['environmental'] } },
        worldbank: { enabled: true, sync_frequency: 'weekly', filters: { keywords: ['environmental'] } },
        usa_awards: { enabled: false, sync_frequency: 'weekly', filters: { keywords: ['environmental', 'technology'] } },
        usa_budgets: { enabled: false, sync_frequency: 'weekly', filters: {} },
        federal_register: { enabled: false, sync_frequency: 'weekly', filters: { keywords: ['environmental', 'procurement'] } },
        executive_orders: { enabled: false, sync_frequency: 'weekly', filters: { keywords: ['procurement', 'technology'] } },
        rss_feeds: { enabled: true, sync_frequency: 'hourly', filters: { keywords: ['government', 'procurement'] } },
        bc_bid: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], provinces: ['BC'] } },
        bids_tender: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], provinces: ['BC', 'ON'] } },
        bonfire: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'] } },
        gbof: { enabled: true, sync_frequency: 'daily', filters: { keywords: [], tender_type: 'solicitation', pages: 5 } },
        merx: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'] } },
        sap_ariba: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'] } },
        jaggaer: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], provinces: ['ON'] } },
        manitoba: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], provinces: ['MB'] } },
        saskatchewan: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], provinces: ['SK'] } },
        nrc: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental', 'funding'], countries: ['Canada'] } },
        toronto: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], provinces: ['ON'], cities: ['Toronto'] } },
        ungm: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], countries: ['International'], is_active: true, limit: 500 } },
        adb: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], countries: ['International'], status: 'Active', sort_by: 'deadline', limit: 500 } },
        afdb: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], countries: ['International', 'Africa'], limit: 500, use_playwright: true } },
        city_portals: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'] } },
        vancouver_city_portal: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], cities: ['Vancouver'], provinces: ['BC'] } },
        victoria_city_portal: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], cities: ['Victoria'], provinces: ['BC'] } },
        metrovancouver_city_portal: { enabled: true, sync_frequency: 'daily', filters: { keywords: ['environmental'], cities: ['Metro Vancouver'], provinces: ['BC'] } },
      };

      // Merge: policy pack > persisted > defaults
      const mergedConfigs: Record<DataSourceType, DataSourceConfig> = {} as any;
      
      for (const sourceType of Object.keys(defaultConfigs) as DataSourceType[]) {
        const policyPackConfig = policyPackConfigs?.[sourceType];
        const persistedSource = apiIntegrationSources.find(s => {
          const meta = s.meta || s.metadata || {};
          const integrationType = meta.integration_type || s.name;
          return integrationType === sourceType || s.name === sourceType;
        });
        const persistedConfig = persistedSource ? {
          enabled: (persistedSource.meta || persistedSource.metadata)?.enabled ?? true,
          sync_frequency: (persistedSource.meta || persistedSource.metadata)?.sync_frequency || 'daily',
          filters: (persistedSource.meta || persistedSource.metadata)?.filters || {},
        } : null;
        
        mergedConfigs[sourceType] = {
          ...defaultConfigs[sourceType],
          ...persistedConfig,
          ...policyPackConfig,
        };
      }

      return mergedConfigs;
    },
    enabled: !!projectId,
  });
  
  // Load all jobs for the project (for progress tracking)
  const { data: allJobs = [] } = useQuery({
    queryKey: ['all-jobs', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      return await listJobs(projectId);
    },
    enabled: !!projectId,
    refetchInterval: (query) => {
      const jobs = query.state.data || [];
      const hasActiveJobs = jobs.some(job => 
        ['queued', 'running', 'pending'].includes(job.status)
      );
      return hasActiveJobs ? 3000 : false; // Poll every 3s if active jobs
    },
  });
  
  // One GROUP BY of opportunity source tags. Do not N+1 /sources/{id}/entity-count.
  const { data: sourceEntityCounts = {} } = useQuery({
    queryKey: ['source-entity-counts', projectId],
    queryFn: async () => {
      if (!projectId) return {};
      const result = await getEntitySources(projectId, 'govt_opportunity');
      return entityCountsBySourceId(result.sources || []);
    },
    enabled: !!projectId,
    refetchInterval: 30000,
  });
  
  // Preview mutation - calls POST /sources/{sourceId}/sync-preview
  const previewMutation = useMutation({
    mutationFn: async ({ source, config }: { source: DataSourceType; config: DataSourceConfig }) => {
      const startTime = Date.now();
      
      console.log(`[PREVIEW] Starting preview for ${source}...`);
      
      // Step 1: Find or create source
      const sources = await listSources(projectId!);
      let integrationSource = findSourceByIntegrationType(sources, source);
      
      if (!integrationSource) {
        console.log(`[PREVIEW] Source not found, creating new source for ${source}...`);
        const apiConfig: Record<string, any> = {};
        if (source === 'samgov' && config.api_key) {
          apiConfig.api_key = config.api_key;
        }
        if (source === 'rss_feeds') {
          apiConfig.feed_urls = RSS_FEED_REGISTRY.map(feed => feed.url);
        }
        
        const apiFilters: Record<string, any> = {};
        if (config.filters?.keywords) apiFilters.keywords = config.filters.keywords;
        if (config.filters?.startDate) apiFilters.posted_from = config.filters.startDate;
        if (config.limit) apiFilters.limit = config.limit;
        
        integrationSource = await registerAPIIntegrationSource({
          project_id: projectId!,
          name: source,
          description: `${source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} API Integration`,
          integration_type: source as any,
          api_config: apiConfig,
          filters: apiFilters,
          sync_frequency: 'daily',
          enabled: true,
        });
        console.log(`[PREVIEW] Created source: ${integrationSource.id}`);
      } else {
        console.log(`[PREVIEW] Using existing source: ${integrationSource.id}`);
      }
      
      // Step 2: Call preview endpoint
      console.log(`[PREVIEW] Calling previewSyncAPIIntegration(${integrationSource.id})...`);
      const previewResult = await previewSyncAPIIntegration(integrationSource.id, config.filters || {});
      const responseTime = Date.now() - startTime;
      
      console.log(`[PREVIEW] Preview result:`, previewResult);
      
      // Convert preview result to preview data format
      const result = {
        opportunities: [],
        awards: [],
        estimatedCount: previewResult.estimated_count,
        lastSync: previewResult.last_sync,
      };
      
      return { source, result, responseTime, previewResult };
    },
    onSuccess: async ({ source, result, responseTime, previewResult }) => {
      const estimatedCount = previewResult?.estimated_count || result.estimatedCount || 0;
      setPreviewData(prev => ({ 
        ...prev, 
        [source]: {
          ...result,
          estimatedCount,
          lastSync: previewResult?.last_sync,
        }
      }));
      
      const metrics = {
        responseTime,
        lastSync: new Date().toISOString(),
        itemCount: estimatedCount,
        status: 'healthy' as const,
      };
      
      setSourceMetrics(prev => ({ ...prev, [source]: metrics }));
      
      try {
        const sourceId = await ensureSourceExists(projectId!, source, configurations?.[source] || {} as DataSourceConfig);
        await updateSourceMetadata(sourceId, {
          health_metrics: {
            status: 'healthy',
            message: `Preview successful: ${estimatedCount} items estimated`,
            response_time_ms: responseTime,
            last_checked: metrics.lastSync,
          },
          sync_metrics: {
            last_sync_at: metrics.lastSync,
            item_count: metrics.itemCount,
          },
          enabled: true,
        });
        queryClient.invalidateQueries({ queryKey: ['integration-sources', projectId] });
      } catch (error) {
        console.error(`[Preview] Failed to save state:`, error);
      }
    },
    onError: async (error: any, variables) => {
      setPreviewData(prev => ({ 
        ...prev, 
        [variables.source]: { 
          opportunities: [], 
          awards: [],
          error: error.message || 'Failed to fetch preview data'
        } 
      }));
      
      setSourceMetrics(prev => ({
        ...prev,
        [variables.source]: {
          responseTime: error.responseTime || 0,
          lastSync: new Date().toISOString(),
          itemCount: 0,
          status: 'error' as const,
        }
      }));
    },
  });
  
  // Sync mutation - directly calls POST /sources/{sourceId}/sync
  const syncMutation = useMutation({
    mutationFn: async ({ source, config, filters }: { source: DataSourceType; config: DataSourceConfig; filters?: Record<string, any> }) => {
      const startTime = Date.now();
      const sourceName = source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      console.log(`[SYNC] Starting sync for ${source}...`);
      
      // Step 1: Find or create source
      console.log(`[SYNC] Listing sources for project ${projectId}...`);
      const sources = await listSources(projectId!);
      let integrationSource = findSourceByIntegrationType(sources, source);
      
      if (!integrationSource) {
        console.log(`[SYNC] Source not found, creating new source for ${source}...`);
        const apiConfig: Record<string, any> = {};
        if (source === 'samgov' && config.api_key) {
          apiConfig.api_key = config.api_key;
        }
        if (source === 'rss_feeds') {
          apiConfig.feed_urls = RSS_FEED_REGISTRY.map(feed => feed.url);
        }
        
        const apiFilters: Record<string, any> = {};
        if (config.filters?.keywords) apiFilters.keywords = config.filters.keywords;
        if (config.filters?.startDate) apiFilters.posted_from = config.filters.startDate;
        if (config.limit) apiFilters.limit = config.limit;
        if (filters) {
          Object.assign(apiFilters, filters);
        }
        
        integrationSource = await registerAPIIntegrationSource({
          project_id: projectId!,
          name: source,
          description: `${sourceName} API Integration`,
          integration_type: source as any,
          api_config: apiConfig,
          filters: apiFilters,
          sync_frequency: 'daily',
          enabled: true,
        });
        console.log(`[SYNC] Created source: ${integrationSource.id}`);
      } else {
        console.log(`[SYNC] Using existing source: ${integrationSource.id}`);
        
        // Update filters if provided (filters are stored in source.meta.filters)
        if (filters && Object.keys(filters).length > 0) {
          console.log(`[SYNC] Updating source filters...`);
          // Note: updateSourceMetadata doesn't update meta directly
          // Filters will be applied during sync via the API endpoint
          console.log(`[SYNC] Filters will be applied during sync:`, filters);
        }
      }
      
      // Step 2: Trigger sync via POST /sources/{sourceId}/sync
      console.log(`[SYNC] Calling triggerSync(${integrationSource.id})...`);
      const syncResult = await triggerSync(integrationSource.id);
      const responseTime = Date.now() - startTime;
      
      console.log(`[SYNC] Sync triggered successfully:`, {
        jobId: syncResult.job_id,
        sourceId: integrationSource.id,
        responseTime,
      });
      
      return { 
        source, 
        jobId: syncResult.job_id,
        responseTime,
        sourceId: integrationSource.id,
      };
    },
    onSuccess: async ({ source, jobId, responseTime, sourceId }) => {
      const sourceName = source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      alert(`✅ Sync Job Queued for ${sourceName}!\n\nJob ID: ${jobId}\n\nCheck the Jobs page to monitor progress.`);
      
      queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
      queryClient.invalidateQueries({ queryKey: ['integration-sources', projectId] });
      
      setSourceMetrics(prev => ({
        ...prev,
        [source]: {
          responseTime,
          lastSync: new Date().toISOString(),
          itemCount: 0,
          status: 'healthy' as const,
        }
      }));
    },
    onError: (error: any, variables) => {
      alert(`❌ Sync Failed for ${variables.source}\n\n${error.message}\n\nCheck console for details.`);
    },
  });
  
  // Build integration status list
  const integrations: IntegrationStatus[] = allSources.map(entry => {
    const userConfig = configurations?.[entry.metadata.id];
    const defaultConfig = dataSourceRegistry.getDefaultConfig(entry.metadata.id) || {};
    
    const persistedSource = findSourceByIntegrationType(persistedSources || [], entry.metadata.id);
    const meta = persistedSource ? (persistedSource.meta || persistedSource.metadata || {}) : {};
    const persistedHealth = meta.health_metrics;
    const persistedSync = meta.sync_metrics;
    const persistedEnabled = meta.enabled;
    
    let enabled = persistedEnabled ?? userConfig?.enabled ?? defaultConfig?.enabled ?? false;
    let apiKey = userConfig?.api_key || defaultConfig?.api_key;
    
    if (entry.metadata.id === 'samgov') {
      const samGovState = resolveSamGovIntegrationState({
        persistedEnabled,
        userEnabled: userConfig?.enabled,
        defaultEnabled: defaultConfig?.enabled,
        userApiKey: userConfig?.api_key,
        defaultApiKey: defaultConfig?.api_key,
      });
      enabled = samGovState.enabled;
      apiKey = samGovState.api_key;
    }
    
    const config: DataSourceConfig = {
      enabled,
      sync_frequency: userConfig?.sync_frequency || defaultConfig?.sync_frequency || 'daily',
      filters: userConfig?.filters || defaultConfig?.filters || {},
      api_key: apiKey,
      api_url: userConfig?.api_url || defaultConfig?.api_url,
    };
    
    let health: HealthCheckResult | null = null;
    if (persistedHealth) {
      const healthStatus = persistedHealth.status;
      if (healthStatus && healthStatus !== 'unknown') {
        health = {
          healthy: healthStatus === 'healthy',
          status: healthStatus,
          message: persistedHealth.message || '',
          response_time_ms: persistedHealth.response_time_ms || 0,
          last_checked: persistedHealth.last_checked || new Date().toISOString(),
        };
      }
    } else {
      const hasSyncData = persistedSource?.last_sync_at || persistedSync?.last_sync_at;
      const hasItems = (persistedSync?.item_count || 0) > 0;
      
      if (enabled && (hasSyncData || hasItems)) {
        health = {
          healthy: true,
          status: 'healthy',
          message: hasItems ? `Last synced: ${persistedSync?.item_count || 0} items` : 'Source is active',
          response_time_ms: persistedHealth?.response_time_ms || 0,
          last_checked: persistedSource?.last_sync_at || persistedSync?.last_sync_at || new Date().toISOString(),
        };
      }
    }
    
    return {
      source: entry.metadata.id,
      enabled: config.enabled,
      health: health,
      lastSync: persistedSource?.last_sync_at || persistedSync?.last_sync_at || null,
      opportunitiesSynced: persistedSync?.item_count || 0,
      config: config as DataSourceConfig,
    };
  });
  
  // Filter integrations (admin_tech can see all; others see enabled + registered sources)
  const filteredIntegrations = integrations.filter(integration => {
    const entry = allSources.find(s => s.metadata.id === integration.source);
    if (!entry) return false;

    const isRegistered = !!findSourceByIntegrationType(persistedSources || [], integration.source);

    if (!canPreviewSync) {
      // Hide disabled sources unless they are registered in the project
      if (!integration.enabled && !isRegistered) return false;
      // Registered sources are visible before first sync/health check
      if (!isRegistered) {
        if (integration.health?.status?.toLowerCase() === 'unknown') return false;
        if (integration.enabled && !integration.health) return false;
      }
    }

    const matchesSearch =
      entry.metadata.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.metadata.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const effectiveStatus = !canPreviewSync && filterStatus === 'disabled' ? 'all' : filterStatus;
    const matchesFilter =
      effectiveStatus === 'all' ||
      (effectiveStatus === 'active' && integration.enabled && integration.health?.healthy) ||
      (effectiveStatus === 'unhealthy' && integration.enabled && !integration.health?.healthy) ||
      (effectiveStatus === 'disabled' && !integration.enabled);
    
    const matchesZone = dataSourceMatchesGeographicZone(
      integration.source,
      selectedGeographicZone,
      userCountryAccess
    );
    
    return matchesSearch && matchesFilter && matchesZone;
  });
  
  // Stats
  const activeCount = integrations.filter(i => i.enabled && i.health?.healthy).length;
  const unhealthyCount = integrations.filter(i => i.enabled && !i.health?.healthy).length;
  const disabledCount = integrations.filter(i => !i.enabled).length;
  
  // Handlers
  const handleOpenSyncModal = async (integration: IntegrationStatus) => {
    try {
      const sources = await listSources(projectId!);
      let integrationSource = findSourceByIntegrationType(sources, integration.source);
      
      if (!integrationSource) {
        const sourceId = await ensureSourceExists(projectId!, integration.source, integration.config);
        integrationSource = { id: sourceId, name: integration.source };
      }
      
      setSyncModalSource({
        source: integration.source,
        sourceId: integrationSource.id,
        sourceName: allSources.find(s => s.metadata.id === integration.source)?.metadata.name || integration.source,
        integrationType: integration.source,
        config: integration.config,
      });
      setSyncModalOpen(true);
    } catch (error) {
      console.error('Failed to open sync modal:', error);
      alert('Failed to open sync settings. Check console for details.');
    }
  };
  
  // Get existing credentials from source metadata
  const getExistingCredentials = (source: DataSourceType): Record<string, string> => {
    if (!persistedSources) return {};
    
    const sourceRecord = findSourceByIntegrationType(persistedSources, source);
    if (!sourceRecord) return {};
    
    const meta = sourceRecord.meta || sourceRecord.metadata || {};
    return meta.credentials || {};
  };
  
  // Get credential display text (e.g., "X as username and ... as password")
  const getCredentialDisplayText = (source: DataSourceType): string | null => {
    const credentials = getExistingCredentials(source);
    if (!credentials || Object.keys(credentials).length === 0) {
      return null;
    }
    
    const parts: string[] = [];
    if (credentials.username) {
      parts.push(`${credentials.username} as username`);
    }
    if (credentials.password) {
      const maskedPassword = '•'.repeat(Math.min(credentials.password.length, 8));
      parts.push(`${maskedPassword} as password`);
    }
    if (credentials.api_key) {
      const maskedKey = credentials.api_key.substring(0, 4) + '•'.repeat(8);
      parts.push(`${maskedKey} as API key`);
    }
    
    return parts.length > 0 ? parts.join(' and ') : null;
  };
  
  const handleOpenCredentialModal = async (integration: IntegrationStatus) => {
    try {
      const sources = await listSources(projectId!);
      let integrationSource = findSourceByIntegrationType(sources, integration.source);
      
      if (!integrationSource) {
        const sourceId = await ensureSourceExists(projectId!, integration.source, integration.config);
        integrationSource = { id: sourceId, name: integration.source };
      }
      
      setCredentialModalSource({
        source: integration.source,
        sourceId: integrationSource.id,
        sourceName: allSources.find(s => s.metadata.id === integration.source)?.metadata.name || integration.source,
        integrationType: integration.source,
        config: integration.config,
      });
      setCredentialModalOpen(true);
    } catch (error) {
      console.error('Failed to open credential modal:', error);
      alert('Failed to open credential management. Check console for details.');
    }
  };
  
  const handleSaveCredentials = async (credentials: Record<string, string>) => {
    if (!credentialModalSource || !projectId) return;
    
    try {
      // Update source metadata with credentials
      await updateSourceMetadata(credentialModalSource.sourceId, {
        credentials: credentials,
        credentials_updated_at: new Date().toISOString(),
      });
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['integration-sources', projectId] });
      queryClient.invalidateQueries({ queryKey: ['integration-configs', projectId] });
      
      console.log(`[Credential Management] Saved credentials for ${credentialModalSource.sourceName}`);
    } catch (error: any) {
      console.error('[Credential Management] Failed to save credentials:', error);
      throw error;
    }
  };
  
  const handlePreview = (integration: IntegrationStatus) => {
    // Override limit to 10 for preview (much faster and sufficient for testing)
    const previewConfig = { ...integration.config, limit: 10 };
    previewMutation.mutate({ source: integration.source, config: previewConfig });
  };
  
  const handleSync = async (integration: IntegrationStatus) => {
    try {
      // Find or create source first
      const sources = await listSources(projectId!);
      let integrationSource = findSourceByIntegrationType(sources, integration.source);
      
      if (!integrationSource) {
        const sourceId = await ensureSourceExists(projectId!, integration.source, integration.config);
        integrationSource = { id: sourceId, name: integration.source };
      }
      
      // Trigger sync directly via API
      syncMutation.mutate({
        source: integration.source,
        config: integration.config,
        filters: integration.config.filters || {},
      });
    } catch (error) {
      console.error('Failed to trigger sync:', error);
      alert('Failed to trigger sync. Check console for details.');
    }
  };
  
  if (!projectId) {
    return (
      <EmptyState
        icon={<Database className="h-12 w-12" />}
        title="No Project Selected"
        description="Please select a project to view data integrations"
      />
    );
  }
  
  if (loadingSources || loadingConfigs) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards — only admin_tech sees */}
      {canPreviewSync && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Database className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Total Sources</div>
                <div className="text-2xl font-bold">{integrations.length}</div>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Active & Healthy</div>
                <div className="text-2xl font-bold text-green-600">{activeCount}</div>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Unhealthy</div>
                <div className="text-2xl font-bold text-yellow-600">{unhealthyCount}</div>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gray-100 rounded-lg">
                <XCircle className="h-6 w-6 text-gray-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Disabled</div>
                <div className="text-2xl font-bold text-gray-600">{disabledCount}</div>
              </div>
            </div>
          </Card>
        </div>
      )}
      
      {/* Search and Filters — only admin_tech sees */}
      {canPreviewSync && (
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search integrations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterStatus === 'disabled' && !canPreviewSync ? 'all' : filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="all">All Status</option>
              <option value="active">Active & Healthy</option>
              <option value="unhealthy">Unhealthy</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </Card>
      )}
      
      {/* Integrations Grid */}
      {filteredIntegrations.length === 0 ? (
        <EmptyState
          icon={<Database className="h-12 w-12" />}
          title="No Integrations Found"
          description="No data sources match your current filters"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredIntegrations.map((integration) => {
            const entry = allSources.find(s => s.metadata.id === integration.source);
            if (!entry) return null;
            
            const metrics = getMetrics(integration, sourceMetrics);
            const isExpanded = expandedSource === integration.source;
            const preview = previewData[integration.source];
            const isPreviewing = previewMutation.isPending && previewMutation.variables?.source === integration.source;
            const isSyncing = syncMutation.isPending && syncMutation.variables?.source === integration.source;
            
            return (
              <Card key={integration.source} className="p-4 hover:shadow-lg transition-shadow">
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{entry.metadata.name}</h3>
                      <p className="text-sm text-gray-600">{entry.metadata.description}</p>
                    </div>
                    <Badge variant={integration.enabled ? 'success' : 'secondary'}>
                      {integration.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  
                  {/* Metrics */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <span className={`ml-2 font-medium ${metrics.statusColor}`}>
                        {metrics.status ? metrics.status.charAt(0).toUpperCase() + metrics.status.slice(1).toLowerCase() : metrics.status}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Entities:</span>
                      <span className="ml-2 font-medium">
                        {(() => {
                          const sourceId = findSourceByIntegrationType(persistedSources || [], integration.source)?.id;
                          if (!sourceId) return 0;
                          
                          // Get entity count from database (ingested entities)
                          const entityCount = sourceEntityCounts[sourceId] ?? 0;
                          
                          // If entities are ingested, show that count
                          if (entityCount > 0) return entityCount;
                          
                          // Otherwise, check latest completed sync job for item count
                          const latestCompletedJob = allJobs
                            .filter(job => {
                              const jobSourceId = job.input_data?.source_id;
                              const integrationType = job.input_data?.integration_type;
                              return (jobSourceId === sourceId || integrationType === integration.source) &&
                                     job.status === 'completed' &&
                                     job.type === 'index';
                            })
                            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
                          
                          if (latestCompletedJob?.output_data?.item_count) {
                            return latestCompletedJob.output_data.item_count;
                          }
                          
                          // Fallback to metrics.itemCount (from persisted sync metadata)
                          return metrics.itemCount || 0;
                        })()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Response:</span>
                      <span className="ml-2 font-medium">{metrics.responseTime}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-600">Last Sync:</span>
                      <span className="ml-2 text-xs">{metrics.lastSync}</span>
                    </div>
                  </div>
                  
                  {/* Job Progress Display - Show prominently in card */}
                  {(() => {
                    const sourceId = findSourceByIntegrationType(persistedSources || [], integration.source)?.id;
                    if (!sourceId) return null;
                    
                    // Find active jobs for this source (check both sync and ingest jobs)
                    const activeJobs = allJobs.filter(job => {
                      const jobSourceId = job.input_data?.source_id;
                      const integrationType = job.input_data?.integration_type;
                      const isThisSource = jobSourceId === sourceId;
                      const isThisIntegration = integrationType === integration.source;
                      
                      return (isThisSource || isThisIntegration) && 
                             ['queued', 'running', 'pending'].includes(job.status);
                    });
                    
                    const activeJob = activeJobs[0];
                    if (!activeJob) return null;
                    
                    const progress = activeJob.progress_percentage || 0;
                    const stage = activeJob.current_stage || activeJob.status;
                    const message = activeJob.stage_message || activeJob.name || `Processing...`;
                    const jobType = activeJob.type === 'index' ? 'Sync' : activeJob.type === 'ingest' ? 'Ingest' : 'Job';
                    
                    return (
                      <div className="mt-3 pt-3 border-t border-blue-200 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                            {jobType} Job Progress
                          </div>
                          <Badge variant="primary" className="text-xs">
                            {activeJob.status}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-700 dark:text-gray-300">{message}</span>
                            <span className="font-medium text-blue-600 dark:text-blue-400">{progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                            {activeJob.actual_item_count !== null && activeJob.actual_item_count !== undefined && (
                              <div>
                                Processed: <span className="font-medium">{activeJob.actual_item_count}</span>
                              </div>
                            )}
                            {activeJob.expected_item_count && (
                              <div>
                                Expected: <span className="font-medium">{activeJob.expected_item_count}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Actions — only admin_tech (or integrationsTechRoles) can see Preview / Sync / Credentials */}
                  {canPreviewSync && (
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePreview(integration)}
                        disabled={!integration.enabled || isPreviewing}
                      >
                        {isPreviewing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                        <span className="ml-1">Preview</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleSync(integration)}
                        disabled={!integration.enabled || isSyncing}
                      >
                        {isSyncing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                        <span className="ml-1">Sync</span>
                      </Button>
                      {requiresCredentials(integration.source) && (() => {
                        const credentialText = getCredentialDisplayText(integration.source);
                        return (
                          <div className="relative group">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenCredentialModal(integration)}
                              title={credentialText || "Manage credentials"}
                            >
                              <Key className="h-4 w-4" />
                              {credentialText && (
                                <span className="ml-1 text-xs text-gray-600 dark:text-gray-400 max-w-[100px] truncate">
                                  {credentialText.split(' as ')[0]}
                                </span>
                              )}
                            </Button>
                            {credentialText && (
                              <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-10">
                                <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                                  {credentialText}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Preview Data */}
                  {preview && isExpanded && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded text-sm text-gray-900 dark:text-gray-100">
                      {preview.error ? (
                        <p className="text-red-600">{preview.error}</p>
                      ) : (
                        <div>
                          <p className="font-medium">
                            Found {preview.opportunities?.length || 0} opportunities,{' '}
                            {preview.awards?.length || 0} awards
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Sync Settings Modal */}
      {syncModalSource && (
        <SyncSettingsModal
          isOpen={syncModalOpen}
          onClose={() => {
            setSyncModalOpen(false);
            setSyncModalSource(null);
          }}
          sourceId={syncModalSource.sourceId}
          sourceName={syncModalSource.sourceName}
          integrationType={syncModalSource.integrationType}
          initialFilters={syncModalSource.config.filters || {}}
          onConfirm={(filters) => {
            syncMutation.mutate({
              source: syncModalSource.source,
              config: syncModalSource.config,
              filters,
            });
            setSyncModalOpen(false);
            setSyncModalSource(null);
          }}
        />
      )}
      
      {/* Credential Management Modal */}
      {credentialModalSource && (
        <CredentialManagementModal
          isOpen={credentialModalOpen}
          onClose={() => {
            setCredentialModalOpen(false);
            setCredentialModalSource(null);
          }}
          onSave={handleSaveCredentials}
          source={credentialModalSource.source}
          sourceName={credentialModalSource.sourceName}
          credentialFields={getCredentialFields(credentialModalSource.source)}
          existingCredentials={getExistingCredentials(credentialModalSource.source)}
        />
      )}
    </div>
  );
}

// Ensure named export is available
export { IntegrationsTab };
